import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Modal, Button, Badge, Col, Row } from 'react-bootstrap';

import config from '../../../../config.js';

import { getLoggedUser, getUserPreference } from '../../UserActions.js';
import { getUserGroup, getUserGroupOption, getIsUserGroupFromParent, USER_GROUP_DEFAULT } from '../../UserGroupActions.js';
import { getOrdersByUser, getOrdersByUserRequest } from '../../../Order/OrderActions.js';
import { isPopinActive, setPopinActive } from '../../../App/AppActions.js';
import { AFFILIATION_QUERY_PARAM } from '../../../Affiliation/AffiliationActions.js';
import { getProducts, getProductsRequest, getProductsFromStore } from '../../../Product/ProductActions.js';
import { getTranslation } from '../../../Intl/IntlHelper.js';

import { getFile } from '../../../../util/file.js';
import Icon from '../../../../components/Content/Icon.js';
import SocialShare from '../../../../components/Social/SocialShare.js';
import withRouter from '../../../../components/Router/WithRouter.js';

// import './ParrainagePopin.css';

const DISMISS_KEY = 'parrainage-popin-dismissed';
const POINTS = config.fidelity?.pointsPerInvitation || 100;

function ParrainagePopin({ dispatch, intl, search, user, orders, userGroup, isUserGroupFromParent, popinActive, adaptative, fidelityGifts, productsLoaded, locale }) {
    const [open, setOpen] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [showGifts, setShowGifts] = useState(false);

    useEffect(() => {
        if(typeof window !== 'undefined' && sessionStorage.getItem(DISMISS_KEY)) {
            setDismissed(true);
        }
    }, []);

    useEffect(() => {
        if(user && !orders.length) {
            dispatch(getOrdersByUserRequest());
        }
    }, [user]);

    const isIndividual = userGroup?.identifier === USER_GROUP_DEFAULT || isUserGroupFromParent;
    const isEligible = user && orders.length > 0 && isIndividual && getUserGroupOption(userGroup, 'enableFidelity');

    useEffect(() => {
        if(!isEligible || typeof window === 'undefined') return;
        if(search.get('affiliationPopin') !== null && !popinActive) {
            setOpen(true);
            dispatch(setPopinActive(true));
        }
    }, [isEligible]);

    if(!isEligible || dismissed) return null;

    const handleOpen = () => {
        if(!popinActive) {
            setOpen(true);
            dispatch(setPopinActive(true));
        }
    };

    const handleClose = () => {
        setOpen(false);
        dispatch(setPopinActive(false));
    };

    const handleDismiss = () => {
        if(typeof window !== 'undefined') {
            sessionStorage.setItem(DISMISS_KEY, '1');
        }
        setDismissed(true);
        if(open) {
            setOpen(false);
            dispatch(setPopinActive(false));
        }
    };

    const handleToggleGifts = () => {
        if(!showGifts && !productsLoaded) {
            dispatch(getProductsRequest());
        }
        setShowGifts(v => !v);
    };

    const affiliationUrl = `${config.url}${getUserPreference(user, 'language')}/?${AFFILIATION_QUERY_PARAM}=${user._id}&utm_campaign=fidelity-affiliation&utm_medium=invitation&utm_source={network}`;

    return (
        <Fragment>
            <Button variant="warning" size="sm" className="rounded-pill w-100 py-1 my-0" onClick={handleOpen}>
                🎁 <span className={classNames(adaptative && ['d-none', 'd-md-inline'])}><FormattedMessage id="parrainagePillLabel" defaultMessage="Refer a friend" /></span>
            </Button>

            <Modal size="lg" show={open} onHide={handleClose} centered contentClassName="bg-info bg-texture">
                <Modal.Body className="p-4">

                    <div className="text-center pb-3 border-bottom mb-3">
                        <h2 className="h4 fw-semibold text-primary mb-2">
                            <FormattedMessage id="parrainageModalTitle" defaultMessage="Invite a friend, earn rewards" />
                        </h2>
                        <p className="text-muted small mb-0 mx-auto col-10 col-md-8">
                            <FormattedMessage
                                id="parrainageModalIntro"
                                defaultMessage="For every friend who places their first order via your link, you earn <b>{points} loyalty points</b> to redeem for a gift."
                                values={{
                                    points: POINTS,
                                    b: chunks => <strong>{chunks}</strong>,
                                }}
                            />
                        </p>
                    </div>

                    <Row className="align-items-center justify-content-center text-center gx-2 gy-3 border-bottom pb-3 mb-3">
                        <Col xs={12} md className="d-flex flex-column align-items-center gap-2">
                            <span className="fs-2 lh-1" aria-hidden="true">📤</span>
                            <strong className="small fw-bold text-primary">
                                <FormattedMessage id="parrainageStep1Title" defaultMessage="Share" />
                            </strong>
                            <span className="text-muted small">
                                <FormattedMessage id="parrainageStep1" defaultMessage="Send your personal link to friends" />
                            </span>
                        </Col>

                        <Col xs={12} md="auto" className="text-center text-muted">
                            <span className="d-none d-md-block fs-4" aria-hidden="true">→</span>
                            <span className="d-md-none" aria-hidden="true">↓</span>
                        </Col>

                        <Col xs={12} md className="d-flex flex-column align-items-center gap-2">
                            <span className="fs-2 lh-1" aria-hidden="true">🛒</span>
                            <strong className="small fw-bold text-primary">
                                <FormattedMessage id="parrainageStep2Title" defaultMessage="They order" />
                            </strong>
                            <span className="text-muted small">
                                <FormattedMessage id="parrainageStep2" defaultMessage="Your referral places their first order" />
                            </span>
                        </Col>

                        <Col xs={12} md="auto" className="text-center text-muted">
                            <span className="d-none d-md-block fs-4" aria-hidden="true">→</span>
                            <span className="d-md-none" aria-hidden="true">↓</span>
                        </Col>

                        <Col xs={12} md className="d-flex flex-column align-items-center gap-2 border border-warning rounded p-3 bg-warning bg-opacity-10">
                            <span className="fs-2 lh-1" aria-hidden="true">🎁</span>
                            <strong className="fw-bold fs-4 text-danger">
                                +{POINTS} pts
                            </strong>
                            <span className="text-muted small">
                                <FormattedMessage id="parrainageStep3" defaultMessage="to redeem for a gift" />
                            </span>
                        </Col>
                    </Row>

                    <div className="text-center mb-3">
                        <Button variant="link" size="sm" className="text-primary fw-semibold" onClick={handleToggleGifts}>
                            ✨ <FormattedMessage id="parrainageGiftsToggle" defaultMessage="See available gifts" />
                            {' '}{showGifts ? '▲' : '▼'}
                        </Button>
                    </div>

                    {showGifts && (
                        <div className="d-flex flex-wrap gap-2 mb-3 p-3 rounded bg-white bg-opacity-50">
                            {
								fidelityGifts.length === 0
								? (
									<p className="w-100 text-muted small text-center mb-0">
										<FormattedMessage id="parrainageGiftsEmpty" defaultMessage="No gifts available at the moment." />
									</p>
								) : (
									<Row className="gx-2 gy-3">
										{fidelityGifts.map(product => {
											const cover = (product.pictures || [])[0];
											const name = getTranslation(product, 'name', locale);
											return (
												<Col xs="6" md="4" lg="3" key={product._id}>
													<div className="position-relative overflow-hidden rounded" style={{ height: '120px' }}>
														{
															cover
															? <img src={getFile(cover)} alt={name} className="w-100 h-100 object-fit-cover" loading="lazy" />
															: <div className="w-100 h-100 bg-light" aria-hidden="true" />
														}
														<div className="position-absolute top-0 start-0 w-100 text-center pt-1">
															<Badge bg="secondary" pill className="bg-secondary-gradient"><span className="h6">{product.fidelityPoints} pts</span></Badge>
														</div>
													</div>
													<p className="small text-center mt-1 mb-0">{name}</p>
												</Col>
											);
										})}
									</Row>
								)
							}
                        </div>
                    )}

                    <div className="border-top pt-3 text-center">
                        <p className="text-uppercase fw-semibold text-primary small mb-3">
                            <FormattedMessage id="parrainageShareLabel" defaultMessage="Share my link" />
                        </p>
                        <SocialShare
							type="affiliation-fidelity"
                            id={user._id}
                            url={affiliationUrl}
                            title={intl.formatMessage(
                                { id: 'fidelityAffiliationInvitationTitle', defaultMessage: 'I invite you to check the {app} shop' },
                                { app: config.application.name },
                            )}
                            text={intl.formatMessage(
                                { id: 'fidelityAffiliationInvitationText', defaultMessage: 'Hey! I discovered this shop {app} and I think you will love it!' },
                                { app: config.application.name },
                            )}
                        />
                    </div>

                    <div className="text-center mt-3">
                        <Button variant="light" onClick={handleClose} aria-label={intl.formatMessage({ id: 'close', defaultMessage: 'Close' })}>
                            <Icon icon="close" /> <FormattedMessage id="close" defaultMessage="Close" />
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
        </Fragment>
    );
}

function mapStateToProps(store) {
    const user = getLoggedUser(store);
    return {
        user,
        orders: user ? getOrdersByUser(store, user._id) : [],
        userGroup: getUserGroup(store),
        isUserGroupFromParent: getIsUserGroupFromParent(store),
        popinActive: isPopinActive(store),
        productsLoaded: getProductsFromStore(store).length > 0,
        fidelityGifts: getProducts(store, { isActive: true, isAvailable: true })
            .filter(p => p.fidelityPoints > 0)
            .sort((a, b) => a.fidelityPoints - b.fidelityPoints),
        locale: store.intl.locale,
    };
}

ParrainagePopin.defaultProps = {
    user: null,
    userGroup: null,
    isUserGroupFromParent: false,
    orders: [],
    popinActive: false,
    fidelityGifts: [],
    productsLoaded: false,
    locale: 'fr',
};

ParrainagePopin.propTypes = {
    dispatch: PropTypes.func.isRequired,
    intl: PropTypes.object.isRequired,
	search: PropTypes.object.isRequired,
    user: PropTypes.object,
    orders: PropTypes.arrayOf(PropTypes.shape({})),
    fidelityGifts: PropTypes.arrayOf(PropTypes.shape({})),
    productsLoaded: PropTypes.bool,
    locale: PropTypes.string,
    userGroup: PropTypes.object,
    isUserGroupFromParent: PropTypes.bool,
    popinActive: PropTypes.bool,
    adaptative: PropTypes.bool,
};

export default connect(mapStateToProps)(injectIntl(withRouter(ParrainagePopin)));
